import DOMPurify from 'dompurify';

const DANGEROUS_PROTOCOLS = [
    'javascript:',
    'data:',
    'vbscript:',
    'file:',
    'about:',
    'chrome:',
    'chrome-extension:',
    'moz-extension:',
];

// Install the sanitizer plugin
export function install(Vue) {
    // Add global methods
    Vue.prototype.$sanitize = {
        input: sanitizeInput,
        url: sanitizeUrl,
        html: sanitizeHtmlTemplate,
        description: sanitizeResourceDescription,
        css: sanitizeCss
    };

    // Add global mixin for input sanitization
    Vue.mixin({
        methods: {
            sanitizeInput(event, type = 'input') {
                if (!event || !event.target) return;
                const value = event.target.value;
                const sanitizedValue = this.$sanitize[type](value);
                if (value !== sanitizedValue) {
                    event.target.value = sanitizedValue;
                    event.target.dispatchEvent(new Event('input'));
                }
            }
        }
    });
}

export const sanitizeInput = (input) => {
    if (input === null || input === undefined) {
        return '';
    }

    // Handle non-string inputs
    if (typeof input !== 'string') {
        input = String(input);
    }

    // Remove dangerous protocols instead of entire input
    DANGEROUS_PROTOCOLS.forEach(protocol => {
        input = input.replace(new RegExp(protocol, 'gi'), '');
    });

    const config = {
        ALLOWED_TAGS: ['p', 'b', 'i', 'em', 'strong', 'br'],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
        ALLOW_DATA_ATTR: false,
        ADD_ATTR: ['rel'],
        FORCE_BODY: true,
        SANITIZE_DOM: true,
        WHOLE_DOCUMENT: false,
        RETURN_DOM_FRAGMENT: false,
        RETURN_DOM: false,
        ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|xxx):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
    };

    return DOMPurify.sanitize(input, config);
};

export const sanitizeUrl = (url) => {
    if (!url) return '';

    let sanitized = url;
    DANGEROUS_PROTOCOLS.forEach(protocol => {
        sanitized = sanitized.replace(new RegExp(protocol, 'gi'), '');
    });

    sanitized = sanitizeInput(sanitized);
    return sanitized.replace(/[^\w-\.\:\/@\?\&\=\%\#]/gi, '');
};

export const sanitizeHtmlTemplate = (template) => {
    if (!template) return '';

    // Check if the input is a full HTML document
    const isFullDocument = /^\s*<!DOCTYPE|<html/i.test(template);

    const config = {
        ALLOWED_TAGS: [
            // Document structure (only used when input is a full document)
            'html', 'head', 'body', 'title', 'meta', 'style', 'link',
            // Basic formatting
            'p', 'div', 'span', 'br', 'hr',
            // Text styling
            'b', 'i', 'em', 'strong', 'u', 'sub', 'sup', 'font',
            // Lists
            'ul', 'ol', 'li',
            // Tables
            'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
            // Links and Images
            'a', 'img',
            // Email-specific
            'center', 'spacer', '!doctype', 'xml', 'o:officedocumentsettings', 'o:allowpng', 'o:pixelsperinch',
            // Microsoft Office namespace elements
            'v:*', 'o:*', 'w:*',
            // Comments for email client compatibility
            '!--[if', '![endif]--', '!--', '--'
        ],
        ALLOWED_ATTR: [
            // Global attributes
            'class', 'id', 'style', 'align', 'valign', 'dir', 'lang',
            // Table attributes
            'cellpadding', 'cellspacing', 'border', 'bgcolor', 'width', 'height',
            // Link attributes
            'href', 'target', 'rel', 'name',
            // Image attributes
            'src', 'alt', 'title', 'border', 'hspace', 'vspace', 'style',
            // Email-specific attributes
            'xmlns', 'xmlns:v', 'xmlns:o', 'xmlns:w', 'background',
            // Meta attributes
            'http-equiv', 'content', 'charset',
            // Style attributes
            'media', 'type',
            // Microsoft Office specific attributes
            'urn:schemas-microsoft-com:office:office',
            // Additional email template attributes
            'role', 'aria-*', 'data-*',
            // Layout attributes
            'colspan', 'rowspan', 'scope',
            // Font attributes
            'face', 'size', 'color'
        ],
        ALLOW_DATA_ATTR: true,
        ADD_ATTR: ['target'],
        FORBID_ATTR: [
            // Event handlers that could contain malicious code
            'onabort', 'onblur', 'onchange', 'onclick', 'ondblclick', 'onerror', 'onfocus',
            'onkeydown', 'onkeypress', 'onkeyup', 'onload', 'onmousedown', 'onmousemove',
            'onmouseout', 'onmouseover', 'onmouseup', 'onreset', 'onresize', 'onscroll',
            'onselect', 'onsubmit', 'onunload'
        ],
        FORBID_TAGS: [
            // Potentially dangerous tags
            'script', 'object', 'embed', 'param', 'iframe', 'frame', 'frameset',
            'form', 'input', 'button', 'textarea', 'select', 'option'
        ],
        SAFE_FOR_TEMPLATES: true,
        WHOLE_DOCUMENT: isFullDocument, // Only true for full HTML documents
        SANITIZE_DOM: true,
        KEEP_CONTENT: true,
        RETURN_DOM_FRAGMENT: false,
        RETURN_DOM: false,
        // Allow common email template URL patterns
        ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|maps|data|cid):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
    };

    // First pass: Remove dangerous protocols
    let sanitized = template;
    DANGEROUS_PROTOCOLS.forEach(protocol => {
        sanitized = sanitized.replace(new RegExp(protocol, 'gi'), '');
    });

    // Second pass: DOMPurify sanitization with email-friendly config
    return DOMPurify.sanitize(sanitized, config);
};

export const sanitizeResourceDescription = (description) => {
    const config = {
        ALLOWED_TAGS: ['a', 'strong', 'em', 'br'],
        ALLOWED_ATTR: ['href', 'target', 'style'],
        ALLOW_DATA_ATTR: false,
        FORCE_BODY: true
    };

    return DOMPurify.sanitize(description, config);
};

export const sanitizeCss = (css) => {
    if (!css) return '';

    return css
        .replace(/\/\*[\s\S]*?\*\//g, '')
        .replace(/@import\s+[^;]*;?/g, '')
        .replace(/javascript:/gi, '')
        .replace(/expression\s*\([^)]*\)/gi, '')
        .replace(/behavior\s*:[^;]*;?/gi, '')
        .replace(/-moz-binding\s*:[^;]*;?/gi, '')
        .replace(/url\s*\(\s*[^)]*\)/gi, (match) => {
            return DANGEROUS_PROTOCOLS.some(protocol =>
                match.toLowerCase().includes(protocol)) ? '' : match;
        });
};
